import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, XCircle, Clock, Eye, ChevronDown, ChevronUp, RefreshCw, Loader2, Send, Share2, Languages, Instagram, Facebook, Linkedin, Music2, Palette, Smartphone, Image as ImageIcon, Sparkles, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import CommentsThread from "@/components/CommentsThread";
import TranslateDialog from "@/components/TranslateDialog";
import TikTokPublishDialog from "@/components/TikTokPublishDialog";
import PlatformMockup from "@/components/PlatformMockup";
import { CreateReelDialog } from "@/components/CreateReelDialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDateCH } from "@/lib/dateFormat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
  instagram: { label: "Instagram", icon: Instagram, color: "#E1306C", bg: "bg-pink-500/10 text-pink-400 border-pink-500/30" },
  facebook:  { label: "Facebook",  icon: Facebook,  color: "#1877F2", bg: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  tiktok:    { label: "TikTok",    icon: Music2,    color: "#000000", bg: "bg-slate-500/10 text-slate-300 border-slate-500/30" },
  linkedin:  { label: "LinkedIn",  icon: Linkedin,  color: "#0A66C2", bg: "bg-sky-500/10 text-sky-400 border-sky-500/30" },
};

const PlatformBadge = ({ platform }) => {
  const meta = PLATFORM_META[platform] || PLATFORM_META.instagram;
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.bg} border text-[10px] gap-1`} data-testid={`platform-badge-${platform || "instagram"}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </Badge>
  );
};

const statusMap = {
  draft: { label: "Entwurf", cls: "status-draft" },
  review: { label: "Zur Pruefung", cls: "status-review" },
  approved: { label: "Freigegeben", cls: "status-approved" },
  scheduled: { label: "Geplant", cls: "status-scheduled" },
  publishing: { label: "Wird veroeffentlicht...", cls: "status-scheduled" },
  published: { label: "Veroeffentlicht", cls: "status-published" },
  failed: { label: "Fehlgeschlagen", cls: "status-failed" },
};

export default function ApprovalPage() {
  const [content, setContent] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [reuseDialog, setReuseDialog] = useState(null);
  const [reuseResult, setReuseResult] = useState(null);
  const [reusing, setReusing] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [tiktokPublish, setTiktokPublish] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  const [focusId, setFocusId] = useState(null);
  const [translateItem, setTranslateItem] = useState(null);
  const cardRefs = useRef({});
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();

  // Read ?focus={content_id} from URL (e.g. from Slack deep-link) on mount + URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("focus");
    if (id) setFocusId(id);
  }, [location.search]);

  const load = async () => {
    try {
      const [c, co, sa] = await Promise.all([
        axios.get(`${API}/content`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/social-accounts`, { withCredentials: true }),
      ]);
      setContent(c.data);
      setCompanies(co.data);
      setAccounts(sa.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  // Auto-scroll + highlight focused card once content is loaded
  useEffect(() => {
    if (!focusId || !content.length) return;
    const timer = setTimeout(() => {
      const el = cardRefs.current[focusId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setExpanded(focusId);
      }
      // Clear focus state after highlight animation (3s)
      setTimeout(() => setFocusId(null), 3000);
    }, 200);
    return () => clearTimeout(timer);
  }, [focusId, content]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/content/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Status auf "${statusMap[status]?.label}" geaendert`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const handlePublish = async (id, tiktokOptions = null) => {
    setPublishing(id);
    try {
      const payload = tiktokOptions ? { tiktok_options: tiktokOptions } : {};
      const r = await axios.post(`${API}/content/${id}/publish`, payload, { withCredentials: true });
      toast.info(r.data?.message || "Veroeffentlichung gestartet...");
      load();
      // Publish laeuft im Hintergrund (Videos brauchen bei Meta 1-5 Min) — Status pollen
      let finished = false;
      for (let i = 0; i < 120; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        if (!mountedRef.current) return;
        const cr = await axios.get(`${API}/content`, { withCredentials: true });
        if (!mountedRef.current) return;
        const item = (cr.data || []).find((c) => c.id === id);
        if (!item) { finished = true; break; }
        if (item.status === "published") {
          toast.success("Erfolgreich veroeffentlicht!");
          finished = true;
          break;
        }
        if (item.status === "failed") {
          toast.error(item.publish_error || "Veroeffentlichung fehlgeschlagen");
          finished = true;
          break;
        }
      }
      if (!finished) toast.info("Dauert laenger als erwartet — Status wird in der Liste aktualisiert.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Veroeffentlichung fehlgeschlagen - pruefe Social Media Konfiguration");
    }
    setPublishing(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Content wirklich loeschen?")) return;
    try {
      await axios.delete(`${API}/content/${id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch { toast.error("Fehler"); }
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";

  const handleReuse = async (id) => {
    setReusing(true);
    setReuseDialog(id);
    try {
      const res = await axios.post(`${API}/content/${id}/reuse`, {}, { withCredentials: true });
      setReuseResult(res.data);
    } catch (e) {
      toast.error("Fehler bei der Wiederverwendung");
      setReuseDialog(null);
    }
    setReusing(false);
  };

  const saveReusedContent = async (variant) => {
    const original = content.find((c) => c.id === reuseDialog);
    if (!original) return;
    try {
      await axios.post(`${API}/content`, {
        company_id: original.company_id,
        type: original.type,
        platform: original.platform,
        goal: original.goal,
        format_type: original.format_type,
        status: "draft",
        media_ids: original.media_ids || [],
        selected_content: { caption: variant, hashtags: reuseResult?.hashtags || [] },
        reused_from: original.id,
      }, { withCredentials: true });
      toast.success("Neuer Content aus Wiederverwendung erstellt");
      setReuseDialog(null);
      setReuseResult(null);
      load();
    } catch { toast.error("Fehler"); }
  };

  const accountFor = (item) =>
    accounts.find(
      (a) => a.company_id === item.company_id && a.platform === (item.platform || "instagram")
    ) || null;

  const visibleContent = companyFilter === "all"
    ? content
    : content.filter((c) => c.company_id === companyFilter);
  const reviewItems = visibleContent.filter((c) => c.status === "review");
  const otherItems = visibleContent.filter((c) => c.status !== "review");

  return (
    <div data-testid="approval-page" className="space-y-6 max-w-5xl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">{t("approval.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{t("approval.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={companyFilter} onValueChange={setCompanyFilter}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="approval-company-filter">
              <SelectValue placeholder="Firma waehlen..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen ({content.length})</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} ({content.filter((x) => x.company_id === c.id).length})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CreateReelDialog companies={companies} onCreated={load} />
          <CreatePostsQuickButton companies={companies} onCreated={load} />
        </div>
      </div>

      {/* Pending Review */}
      <div>
        <h2 className="text-lg font-semibold font-heading mb-3 flex items-center gap-2">
          <Clock className="w-5 h-5 text-amber-600" />
          {t("approval.pending")} ({reviewItems.length})
        </h2>
        {reviewItems.length === 0 ? (
          <Card className="border-[var(--studio-border)] shadow-none">
            <CardContent className="py-8 text-center text-[var(--studio-text-muted)] text-sm">
              {t("approval.no_pending")}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reviewItems.map((item) => (
              <ContentCard
                key={item.id}
                item={item}
                companyName={companyName(item.company_id)}
                account={accountFor(item)}
                companies={companies}
                expanded={expanded === item.id}
                onToggle={() => setExpanded(expanded === item.id ? null : item.id)}
                onApprove={() => updateStatus(item.id, "approved")}
                onReject={() => updateStatus(item.id, "draft")}
                onSchedule={() => updateStatus(item.id, "scheduled")}
                onUpdateStatus={(newStatus) => updateStatus(item.id, newStatus)}
                onDelete={() => handleDelete(item.id)}
                onReuse={() => handleReuse(item.id)}
                onTranslate={() => setTranslateItem(item)}
                focusId={focusId}
                cardRefs={cardRefs}
                currentUserId={user?.user_id}
              />
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* All Content */}
      <div>
        <h2 className="text-lg font-semibold font-heading mb-3">{t("approval.all_content")} ({otherItems.length})</h2>
        {otherItems.length === 0 ? (
          <p className="text-sm text-[var(--studio-text-muted)]">Keine weiteren Inhalte.</p>
        ) : (
          <div className="space-y-3">
            {otherItems.map((item) => (
              <ContentCard
                key={item.id}
                item={item}
                companyName={companyName(item.company_id)}
                account={accountFor(item)}
                companies={companies}
                expanded={expanded === item.id}
                onToggle={() => setExpanded(expanded === item.id ? null : item.id)}
                onApprove={() => updateStatus(item.id, "approved")}
                onReject={() => updateStatus(item.id, "draft")}
                onSchedule={() => updateStatus(item.id, "scheduled")}
                onPublish={() => (item.platform === "tiktok" ? setTiktokPublish(item) : handlePublish(item.id))}
                onUpdateStatus={(newStatus) => updateStatus(item.id, newStatus)}
                onDelete={() => handleDelete(item.id)}
                onReuse={() => handleReuse(item.id)}
                onTranslate={() => setTranslateItem(item)}
                publishing={publishing === item.id}
                focusId={focusId}
                cardRefs={cardRefs}
                currentUserId={user?.user_id}
              />
            ))}
          </div>
        )}
      </div>

      {/* TikTok Publish Dialog (Audit-konform: Privacy, Interaktionen, Consent) */}
      <TikTokPublishDialog
        open={!!tiktokPublish}
        companyId={tiktokPublish?.company_id}
        publishing={!!tiktokPublish && publishing === tiktokPublish.id}
        onClose={() => setTiktokPublish(null)}
        onConfirm={(opts) => {
          const id = tiktokPublish.id;
          setTiktokPublish(null);
          handlePublish(id, opts);
        }}
      />

      {/* Reuse Dialog */}
      <Dialog open={!!reuseDialog} onOpenChange={(v) => { if (!v) { setReuseDialog(null); setReuseResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Content wiederverwenden</DialogTitle>
          </DialogHeader>
          {reusing ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#2563EB] mb-3" />
              <p className="text-sm text-[var(--studio-text-muted)]">KI generiert neue Varianten...</p>
            </div>
          ) : reuseResult ? (
            <div className="space-y-3 mt-2">
              <p className="text-xs text-[var(--studio-text-muted)]">Original: {reuseResult.original_caption?.substring(0, 100)}...</p>
              <Separator />
              <p className="text-sm font-medium">Neue Varianten:</p>
              {(reuseResult.variants || []).map((v, i) => (
                <button key={i} onClick={() => saveReusedContent(v)} data-testid={`reuse-variant-${i}`}
                  className="w-full text-left p-3 rounded-lg border border-[var(--studio-border)] hover:border-[#2563EB] transition-all text-sm">
                  {v}
                </button>
              ))}
              {reuseResult.hashtags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {reuseResult.hashtags.map((h, i) => <Badge key={i} variant="secondary" className="text-xs">{h}</Badge>)}
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <TranslateDialog
        text={translateItem?.selected_content?.caption || ""}
        open={!!translateItem}
        onOpenChange={(v) => { if (!v) setTranslateItem(null); }}
      />
    </div>
  );
}

function ContentCard({ item, companyName, account = null, companies = [], expanded, onToggle, onApprove, onReject, onSchedule, onPublish, onUpdateStatus, onDelete, onReuse, onTranslate, publishing, focusId, cardRefs, currentUserId }) {
  const st = statusMap[item.status] || statusMap.draft;
  const sc = item.selected_content || {};
  const platform = item.platform || "instagram";
  const needsImage = platform === "instagram" && !(item.media_ids || []).length;
  const needsVideo = platform === "tiktok" && !(item.media_ids || []).length;

  return (
    <Card
      ref={(el) => { if (el) cardRefs.current[item.id] = el; }}
      className={`border-[var(--studio-border)] shadow-none transition-all ${
        focusId === item.id ? "ring-2 ring-[#2563EB] ring-offset-2 animate-pulse" : ""
      }`}
      data-testid={`content-card-${item.id}`}
      data-focused={focusId === item.id ? "true" : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className={`${st.cls} border-0 text-xs`}>{st.label}</Badge>
            <PlatformBadge platform={item.platform} />
            <div>
              <p className="font-medium text-sm">{companyName} &mdash; {item.format_type || item.type}</p>
              <p className="text-xs text-[var(--studio-text-muted)]">
                Ziel: {item.goal} · Erstellt {formatDateCH(item.created_at)}
                {item.schedule_date && ` · Geplant ${formatDateCH(item.schedule_date)}${item.schedule_time ? `, ${item.schedule_time}` : ""}`}
              </p>
              {["instagram", "facebook"].includes(platform) && (
                account ? (
                  <p className="text-[11px] text-emerald-700 font-medium" data-testid={`target-account-${item.id}`}>
                    → postet auf {platform === "instagram" ? "@" : ""}{account.account_name}
                  </p>
                ) : (
                  <p className="text-[11px] text-amber-600 font-medium" data-testid={`no-account-${item.id}`}>
                    ⚠ Kein {PLATFORM_META[platform]?.label}-Konto fuer diese Firma verbunden
                  </p>
                )
              )}
              {needsImage && item.status !== "published" && (
                <p className="text-[11px] text-amber-600 font-medium" data-testid={`needs-image-${item.id}`}>
                  ⚠ Kein Medium — Instagram benoetigt ein Bild oder Video
                </p>
              )}
              {needsVideo && item.status !== "published" && (
                <p className="text-[11px] text-red-600 font-medium" data-testid={`needs-video-${item.id}`}>
                  ⛔ Kein Video — TikTok erlaubt NUR Videos (MP4/MOV). Freigabe gesperrt, bis ein Video zugewiesen ist.
                </p>
              )}
              {item.postponed_reason && item.status === "scheduled" && (
                <p className="text-[11px] text-amber-600" data-testid={`postponed-${item.id}`}>
                  ⏳ {item.postponed_reason}
                </p>
              )}
              {item.cta_link?.url && (
                <p className="text-[11px] text-emerald-700" data-testid={`cta-link-${item.id}`}>
                  🔗 Link im Post: <code className="bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">{item.cta_link.url.replace(/^https?:\/\//, "")}</code>
                  <span className="text-slate-400"> → {item.cta_link.target_url?.replace(/^https?:\/\//, "")}</span>
                  {["instagram", "tiktok"].includes(platform) && (
                    <span className="text-amber-600 font-medium"> · wird als «Link in Bio» erwähnt — diesen Link in die Bio legen</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {item.status === "draft" && (
              <>
                <Button size="sm" variant="outline" onClick={() => onUpdateStatus("review")} data-testid={`submit-review-${item.id}`}
                  className="h-8 text-xs">
                  <Send className="w-3 h-3 mr-1" /> Zur Pruefung
                </Button>
                <Button size="sm" onClick={() => onUpdateStatus("approved")} data-testid={`approve-${item.id}`} disabled={needsVideo}
                  title={needsVideo ? "TikTok benoetigt ein Video" : undefined}
                  className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs disabled:opacity-50">
                  <CheckCircle className="w-3 h-3 mr-1" /> Direkt freigeben
                </Button>
              </>
            )}
            {item.status === "review" && (
              <>
                <Button size="sm" variant="outline" onClick={onReject} data-testid={`reject-${item.id}`}
                  className="text-red-600 hover:bg-red-50 h-8 text-xs">
                  <XCircle className="w-3 h-3 mr-1" /> Ablehnen
                </Button>
                <Button size="sm" onClick={onApprove} data-testid={`approve-${item.id}`} disabled={needsVideo}
                  title={needsVideo ? "TikTok benoetigt ein Video" : undefined}
                  className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs disabled:opacity-50">
                  <CheckCircle className="w-3 h-3 mr-1" /> Freigeben
                </Button>
              </>
            )}
            {item.status === "approved" && (
              <>
                <Button size="sm" variant="outline" onClick={() => onUpdateStatus("draft")} data-testid={`back-to-draft-${item.id}`}
                  className="h-8 text-xs">
                  Zurueckziehen
                </Button>
                <Button size="sm" onClick={onSchedule} data-testid={`schedule-${item.id}`}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs">
                  Planen
                </Button>
                <Button size="sm" onClick={onPublish} disabled={publishing} data-testid={`publish-${item.id}`}
                  className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs">
                  {publishing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Jetzt posten
                </Button>
              </>
            )}
            {item.status === "scheduled" && (
              <>
                <Button size="sm" variant="outline" onClick={() => onUpdateStatus("approved")} data-testid={`unschedule-${item.id}`}
                  className="h-8 text-xs">
                  Planung aufheben
                </Button>
                {onPublish && (
                  <Button size="sm" onClick={onPublish} disabled={publishing} data-testid={`publish-${item.id}`}
                    className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs">
                    {publishing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                    Jetzt posten
                  </Button>
                )}
              </>
            )}
            <Button size="sm" variant="ghost" onClick={onToggle} data-testid={`toggle-${item.id}`}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-3 pt-3 border-t border-[var(--studio-border)] animate-fade-in">
            <ShareReviewLink contentId={item.id} />
            {item.media_ids?.length > 0 && (
              <PostPreview
                contentId={item.id}
                companyId={item.company_id}
                company={companies.find((c) => c.id === item.company_id) || { name: companyName }}
                platform={item.platform || "instagram"}
                mediaIds={item.media_ids}
                applyBrandKit={item.apply_brand_kit}
                caption={sc.caption}
                hashtags={sc.hashtags || []}
              />
            )}
            {sc.caption && (
              <div>
                <p className="text-xs font-medium text-[var(--studio-text-muted)] mb-1">Caption</p>
                <p className="text-sm bg-[var(--studio-bg)] p-3 rounded-lg">{sc.caption}</p>
              </div>
            )}
            {sc.hook && (
              <div>
                <p className="text-xs font-medium text-[var(--studio-text-muted)] mb-1">Hook</p>
                <p className="text-sm bg-[var(--studio-bg)] p-3 rounded-lg">{sc.hook}</p>
              </div>
            )}
            {sc.scenes?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-[var(--studio-text-muted)] mb-1">Szenen</p>
                {sc.scenes.map((s, i) => (
                  <p key={i} className="text-sm bg-[var(--studio-bg)] p-2 rounded mb-1">
                    <span className="font-medium">Szene {s.number}:</span> {s.description}
                  </p>
                ))}
              </div>
            )}
            {sc.hashtags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {sc.hashtags.map((h, i) => (
                  <Badge key={i} variant="secondary" className="text-xs">{h}</Badge>
                ))}
              </div>
            )}
            <div className="flex justify-between">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={onReuse} className="text-xs" data-testid={`reuse-${item.id}`}>
                  <RefreshCw className="w-3 h-3 mr-1" /> Wiederverwenden
                </Button>
                {sc.caption && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onTranslate}
                    className="text-xs"
                    data-testid={`translate-${item.id}`}
                  >
                    <Languages className="w-3 h-3 mr-1" /> Uebersetzen
                  </Button>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={onDelete} className="text-red-500 text-xs" data-testid={`delete-content-${item.id}`}>
                Loeschen
              </Button>
            </div>

            <Separator />
            <CommentsThread contentId={item.id} currentUserId={currentUserId} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ShareReviewLink({ contentId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const createLink = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/content/${contentId}/review-link`,
        { ttl_days: 7 },
        { withCredentials: true }
      );
      const publicUrl = `${window.location.origin}/review/${res.data.token}`;
      setUrl(publicUrl);
      try {
        await navigator.clipboard.writeText(publicUrl);
        toast.success("Review-Link kopiert (7 Tage gueltig)");
      } catch {
        toast.success("Review-Link erstellt");
      }
    } catch (e) {
      toast.error("Fehler beim Erstellen des Links");
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center gap-2 bg-[var(--studio-bg)] p-3 rounded-lg" data-testid={`share-review-${contentId}`}>
      <Share2 className="w-4 h-4 text-[#2563EB]" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium">Client-Review-Link</p>
        {url ? (
          <p className="text-[11px] text-[var(--studio-text-muted)] truncate">{url}</p>
        ) : (
          <p className="text-[11px] text-[var(--studio-text-muted)]">Extern freigeben ohne Login (7 Tage)</p>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={createLink}
        disabled={loading}
        className="h-7 text-xs"
        data-testid={`share-review-btn-${contentId}`}
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : url ? "Erneut kopieren" : "Link erstellen"}
      </Button>
    </div>
  );
}


const PLATFORM_PREVIEW_RATIO = {
  instagram: "aspect-square",     // 1:1
  facebook:  "aspect-[1.91/1]",
  tiktok:    "aspect-[9/16]",
  linkedin:  "aspect-[1.91/1]",
};
const PLATFORM_PREVIEW_LABEL = {
  instagram: "1:1 Feed",
  facebook:  "1.91:1 Feed",
  tiktok:    "9:16 Vertikal",
  linkedin:  "1.91:1 Feed",
};

function PostPreview({ contentId, companyId, company, platform, mediaIds, applyBrandKit, caption, hashtags = [] }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mockupMode, setMockupMode] = useState(true);
  const [isVideo, setIsVideo] = useState(false);
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Inspect first media: videos get poster preview + player, no overlay
        let mediaDoc = null;
        if (mediaIds?.[0]) {
          try {
            const r = await axios.get(`${API}/media/${mediaIds[0]}`, { withCredentials: true });
            mediaDoc = r.data;
          } catch { /* ignore */ }
        }
        const video = !!mediaDoc?.content_type?.startsWith("video/");
        setIsVideo(video);
        if (video) {
          setVideoSrc(`${process.env.REACT_APP_BACKEND_URL}/api/media/file/${mediaDoc.storage_path}`);
          try {
            const tr = await axios.get(`${API}/media/thumb/${mediaDoc.id}`, { withCredentials: true, responseType: "blob" });
            if (cancelled) return;
            const url = URL.createObjectURL(tr.data);
            revokeUrl = url;
            setPreviewUrl(url);
          } catch { setPreviewUrl(null); }
          return;
        }
        // Brand-Overlay deaktiviert — immer das Original-Bild anzeigen
        if (mediaDoc) {
          if (cancelled) return;
          setPreviewUrl(`${process.env.REACT_APP_BACKEND_URL}/api/media/file/${mediaDoc.storage_path}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [contentId, companyId, platform, mediaIds, applyBrandKit]);

  const ratioClass = PLATFORM_PREVIEW_RATIO[platform] || PLATFORM_PREVIEW_RATIO.instagram;
  const ratioLabel = PLATFORM_PREVIEW_LABEL[platform] || PLATFORM_PREVIEW_LABEL.instagram;
  const platformLabel = PLATFORM_META[platform]?.label || "Instagram";

  return (
    <div className="rounded-lg border border-[var(--studio-border)] bg-[var(--studio-bg)] p-3" data-testid={`post-preview-${contentId}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-xs font-medium text-[var(--studio-text-muted)] flex items-center gap-2">
          <Eye className="w-3 h-3" /> Vorschau auf {platformLabel} ({ratioLabel})
        </p>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-[var(--studio-border)] overflow-hidden text-[10px]" data-testid={`preview-mode-toggle-${contentId}`}>
            <button
              onClick={() => setMockupMode(true)}
              className={`px-2 py-1 flex items-center gap-1 ${mockupMode ? "bg-[var(--studio-accent)] text-white" : "text-[var(--studio-text-muted)] hover:bg-white/5"}`}
              data-testid={`preview-mode-mockup-${contentId}`}
            >
              <Smartphone className="w-3 h-3" /> Plattform-UI
            </button>
            <button
              onClick={() => setMockupMode(false)}
              className={`px-2 py-1 flex items-center gap-1 ${!mockupMode ? "bg-[var(--studio-accent)] text-white" : "text-[var(--studio-text-muted)] hover:bg-white/5"}`}
              data-testid={`preview-mode-raw-${contentId}`}
            >
              <ImageIcon className="w-3 h-3" /> Nur Bild
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--studio-text-muted)]" />
        </div>
      ) : mockupMode ? (
        <div className="flex justify-center bg-gradient-to-b from-neutral-100 to-neutral-50 dark:from-neutral-900 dark:to-neutral-950 rounded-md p-4">
          <div className="relative">
            <PlatformMockup
              platform={platform}
              imageUrl={previewUrl}
              caption={caption}
              hashtags={hashtags}
              company={company || {}}
            />
            {isVideo && (
              <button
                onClick={() => setMockupMode(false)}
                className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
                title="Video abspielen"
                data-testid={`preview-play-video-${contentId}`}
              >
                <Play className="w-5 h-5 text-white ml-0.5" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex gap-3">
          <div className={`${ratioClass} w-44 max-w-[40%] rounded-md overflow-hidden bg-black/10 border border-[var(--studio-border)] flex items-center justify-center shrink-0`}>
            {isVideo && videoSrc ? (
              <video
                src={videoSrc}
                controls
                playsInline
                poster={previewUrl || undefined}
                className="w-full h-full object-cover bg-black"
                data-testid={`preview-video-player-${contentId}`}
              />
            ) : previewUrl ? (
              <img src={previewUrl} alt="Post Vorschau" className="w-full h-full object-cover" />
            ) : (
              <span className="text-xs text-[var(--studio-text-muted)]">Kein Bild</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[var(--studio-text-muted)] mb-1">Caption</p>
            <p className="text-sm whitespace-pre-wrap break-words line-clamp-6">
              {caption || <span className="italic text-[var(--studio-text-muted)]">Keine Caption</span>}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


function CreatePostsQuickButton({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [genAiImages, setGenAiImages] = useState(true);
  const [genAiVideos, setGenAiVideos] = useState(false);
  const [allowReuse, setAllowReuse] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [crossPost, setCrossPost] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const ALL_PLATFORMS = [
    { key: "instagram", label: "Instagram" },
    { key: "facebook", label: "Facebook" },
    { key: "tiktok", label: "TikTok" },
    { key: "linkedin", label: "LinkedIn" },
  ];

  const company = companies.find((c) => c.id === companyId);
  const isPreview = !!(result && result.dry_run);

  const togglePlatform = (key) => {
    setResult(null);
    setPlatforms((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
  };

  const selectCompany = (id) => {
    setCompanyId(id);
    setResult(null);
    const co = companies.find((c) => c.id === id);
    setPlatforms(co?.platforms?.length ? co.platforms : ["instagram"]);
  };

  const applyResult = (data, dryRun) => {
    setResult(data);
    if (!dryRun) {
      if ((data?.created?.length || 0) > 0) {
        toast.success(`${data.created.length} Posts erstellt`);
        onCreated && onCreated();
      } else {
        toast.info(data?.message || "Keine Posts erstellt — pruefe Medien & Plan-Einstellungen");
      }
    }
  };

  const run = async (dryRun) => {
    if (!companyId) return;
    if (!platforms.length) {
      toast.error("Bitte mindestens eine Plattform auswaehlen");
      return;
    }
    setBusy(true);
    if (!dryRun) setResult(null); // keep preview while committing
    setPreviewMode(dryRun);
    try {
      const url = `${API}/companies/${companyId}/auto-fill-week${dryRun ? "?dry_run=true" : ""}`;
      const r = await axios.post(
        url,
        { generate_ai_images: genAiImages, generate_ai_videos: genAiVideos, allow_reuse: allowReuse, platforms, cross_post: crossPost },
        { withCredentials: true },
      );
      if (!dryRun && r.data?.job_id) {
        // Background job (AI image generation can take 1-2 min) — poll status
        const jobId = r.data.job_id;
        let finished = false;
        for (let i = 0; i < 100; i++) {
          await new Promise((res) => setTimeout(res, 3000));
          if (!mountedRef.current) return;
          const jr = await axios.get(
            `${API}/companies/${companyId}/auto-fill-jobs/${jobId}`,
            { withCredentials: true },
          );
          if (!mountedRef.current) return;
          if (jr.data.status === "done") {
            applyResult(jr.data.result, false);
            finished = true;
            break;
          }
          if (jr.data.status === "error") {
            toast.error(jr.data.error || "Fehler beim Erstellen");
            setResult({ error: jr.data.error || "Fehler beim Erstellen" });
            finished = true;
            break;
          }
        }
        if (!finished) {
          toast.info("Dauert laenger als erwartet — die Posts erscheinen automatisch in der Liste, sobald sie fertig sind.");
          onCreated && onCreated();
        }
      } else {
        applyResult(r.data, dryRun);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Fehler beim Erstellen";
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
        data-testid="create-posts-quick-btn"
      >
        <Sparkles className="w-4 h-4 mr-2" />
        KI-Posts erstellen
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (!v) { setResult(null); setCompanyId(""); setPreviewMode(false); } } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-posts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#2563EB]" />
              KI-Posts fuer Woche erstellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[var(--studio-text-muted)] uppercase">Firma waehlen</Label>
              <select
                value={companyId}
                onChange={(e) => selectCompany(e.target.value)}
                disabled={busy}
                className="w-full mt-2 px-3 py-2 rounded-md border border-[var(--studio-border)] bg-[var(--studio-bg)] text-sm"
                data-testid="create-posts-company-select"
              >
                <option value="">— bitte waehlen —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {company && !result && (
              <div className="p-3 rounded-md border border-[var(--studio-border)] bg-[var(--studio-bg)] text-xs space-y-1.5">
                <p className="font-medium text-[var(--studio-text)]">Plan fuer {company.name}:</p>
                <p>• Plattformen: <strong>{(company.platforms || ["instagram","facebook"]).join(", ")}</strong></p>
                <p>• {company.posts_per_week ?? 3} Posts + {company.reels_per_week ?? 1} Reels + {company.stories_per_week ?? 0} Stories pro Woche</p>
                <p>• Veroeffentlichungstage: {(company.preferred_days || ["Mo", "Mi", "Fr"]).join(", ")}</p>
                <p>• Auto-Freigabe: {company.auto_approve ? <span className="text-green-700 font-medium">aktiv</span> : "manuell"}</p>
                <p className="text-[10px] text-[var(--studio-text-muted)] mt-2 pt-2 border-t border-[var(--studio-border)]">
                  Tipp: Klick zuerst <strong>Vorschau anzeigen</strong> — die KI generiert den Plan ohne ihn zu speichern. Du kannst dann entscheiden.
                </p>
              </div>
            )}

            {company && (
              <div className="space-y-2">
                <div className="p-2.5 rounded-md border border-[var(--studio-border)]" data-testid="platform-select-row">
                  <p className="text-xs font-medium text-[var(--studio-text)] mb-2">📣 Fuer welche Plattformen erstellen?</p>
                  <div className="flex flex-wrap gap-2">
                    {ALL_PLATFORMS.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => togglePlatform(p.key)}
                        disabled={busy}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                          platforms.includes(p.key)
                            ? "bg-[#2563EB] text-white border-[#2563EB]"
                            : "bg-[var(--studio-bg)] text-[var(--studio-text-muted)] border-[var(--studio-border)] hover:border-[#2563EB]"
                        }`}
                        data-testid={`platform-chip-${p.key}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  {platforms.length > 1 && (
                    <label className="flex items-start gap-2 cursor-pointer mt-2.5 pt-2.5 border-t border-[var(--studio-border)]">
                      <input
                        type="checkbox"
                        checked={crossPost}
                        onChange={(e) => { setCrossPost(e.target.checked); setResult(null); }}
                        className="mt-0.5 accent-[#2563EB]"
                        data-testid="toggle-cross-post"
                      />
                      <div className="text-xs">
                        <p className="font-medium text-[var(--studio-text)]">Jeden Post fuer ALLE gewaehlten Plattformen erstellen (Cross-Post)</p>
                        <p className="text-[10px] text-[var(--studio-text-muted)] mt-0.5">
                          Aus: Plattformen wechseln sich ab. An: jeder Post wird fuer jede Plattform erstellt ({company.posts_per_week ?? 3} Posts × {platforms.length} Plattformen = {(company.posts_per_week ?? 3) * platforms.length}).
                        </p>
                      </div>
                    </label>
                  )}
                </div>
                <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-md border border-[var(--studio-border)] hover:bg-[var(--studio-bg)] transition-colors" data-testid="toggle-ai-images-row">
                  <input
                    type="checkbox"
                    checked={genAiImages}
                    onChange={(e) => setGenAiImages(e.target.checked)}
                    className="mt-0.5 accent-[#2563EB]"
                    data-testid="toggle-ai-images"
                  />
                  <div className="text-xs">
                    <p className="font-medium text-[var(--studio-text)]">🎨 KI-Bilder generieren wenn keine unverwendeten Medien</p>
                    <p className="text-[10px] text-[var(--studio-text-muted)] mt-0.5">Nano-Banana erstellt frische Bilder aus deinem Marken-Profil, wenn alle Uploads bereits in Posts verwendet sind.</p>
                  </div>
                </label>
                {company?.ai_video_enabled && (
                  <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-md border border-[var(--studio-border)] hover:bg-[var(--studio-bg)] transition-colors" data-testid="toggle-ai-videos-row">
                    <input
                      type="checkbox"
                      checked={genAiVideos}
                      onChange={(e) => { setGenAiVideos(e.target.checked); setResult(null); }}
                      className="mt-0.5 accent-[#2563EB]"
                      data-testid="toggle-ai-videos"
                    />
                    <div className="text-xs">
                      <p className="font-medium text-[var(--studio-text)]">🎬 KI-Reel-Video generieren</p>
                      <p className="text-[10px] text-[var(--studio-text-muted)] mt-0.5">
                        Wenn Reels geplant sind aber kein Video vorhanden ist, generiert die KI 1 Reel-Video mit deinem konfigurierten Video-Modell (max. 1 pro Lauf, ~2-5 Min, hoeherer KI-Verbrauch). Hochgeladene Bilder dienen als Vorlage.
                      </p>
                    </div>
                  </label>
                )}
                <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-md border border-[var(--studio-border)] hover:bg-[var(--studio-bg)] transition-colors" data-testid="toggle-allow-reuse-row">
                  <input
                    type="checkbox"
                    checked={allowReuse}
                    onChange={(e) => setAllowReuse(e.target.checked)}
                    className="mt-0.5 accent-[#2563EB]"
                    data-testid="toggle-allow-reuse"
                  />
                  <div className="text-xs">
                    <p className="font-medium text-[var(--studio-text)]">♻️ Bereits verwendete Medien recyceln</p>
                    <p className="text-[10px] text-[var(--studio-text-muted)] mt-0.5">Nutze alle Medien, auch wenn sie schon in anderen Posts referenziert sind.</p>
                  </div>
                </label>
              </div>
            )}

            {result && !result.error && (
              <div className={`rounded-md border text-xs ${isPreview ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"}`} data-testid="create-posts-result">
                <div className={`px-3 py-2 font-medium ${isPreview ? "text-amber-900" : "text-green-900"}`}>
                  {result.message}
                </div>
                {result.available && (
                  <div className="px-3 py-1.5 text-[10px] text-neutral-700 border-t border-[var(--studio-border)] bg-white/40">
                    📦 {result.available.total_uploaded ?? 0} Medien hochgeladen
                    {" · "}{result.available.used ?? 0} bereits in Posts
                    {" · "}{result.available.images} Bilder + {result.available.videos} Videos unverwendet
                    {(result.ai_images_generated || 0) > 0 && <> · 🎨 {result.ai_images_generated} KI-Bilder {isPreview ? "geplant" : "erstellt"}</>}
                  </div>
                )}
                <div className="divide-y divide-[var(--studio-border)] max-h-[40vh] overflow-y-auto">
                  {result.created?.map((c, i) => (
                    <div key={i} className="flex gap-3 p-3" data-testid={`preview-item-${i}`}>
                      {c.thumb_url ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${c.thumb_url}`}
                          alt=""
                          className="w-16 h-16 rounded object-cover shrink-0 border border-[var(--studio-border)]"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded bg-neutral-200 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[9px] uppercase">{c.platform}</Badge>
                          <span className="text-[10px] text-neutral-600">{c.format_type}</span>
                          <span className="text-[10px] text-neutral-500 ml-auto">{c.day_label} {c.schedule_time}</span>
                        </div>
                        <p className="text-[11px] text-neutral-800 line-clamp-2 leading-snug">{c.caption_preview}</p>
                        {c.hashtags?.length > 0 && (
                          <p className="text-[10px] text-blue-600 mt-1 truncate">{c.hashtags.slice(0, 6).join(" ")}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result?.error && (
              <div className="p-3 rounded-md border border-red-200 bg-red-50 text-xs text-red-900">
                {result.error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 flex-wrap">
              {busy && !previewMode && (
                <p className="text-[10px] text-[var(--studio-text-muted)] w-full text-right" data-testid="autofill-progress-hint">
                  ⏳ Posts & KI-Bilder werden erstellt — das kann 1-2 Minuten dauern. Bitte Dialog offen lassen.
                </p>
              )}
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
                Schliessen
              </Button>
              {!isPreview && (
                <Button
                  variant="outline"
                  onClick={() => run(true)}
                  disabled={!companyId || busy}
                  data-testid="create-posts-preview-btn"
                >
                  {busy && previewMode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Vorschau anzeigen
                </Button>
              )}
              <Button
                onClick={() => run(false)}
                disabled={!companyId || busy}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                data-testid="create-posts-run-btn"
              >
                {busy && !previewMode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {busy && !previewMode ? "Erstelle ..." : isPreview ? "Diese Vorschau jetzt speichern" : "Direkt erstellen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
